<script>
/**
 * @component MemberPaymentRulesConfig
 * @description UC-REG-088 — Pantalla de configuración de las reglas de mora
 *              de afiliados de obra social. Permite al admin de la
 *              organización definir, en días desde el último pago, los
 *              umbrales de cada situación: "al día", "mora temprana",
 *              "mora tardía" y "suspendido por falta de pago".
 *              Adicionalmente expone un switch para activar/desactivar el
 *              cambio automático de `status` → `inactive` cuando la
 *              situación pasa a "suspendido por falta de pago".
 *
 *              Cada org puede tener criterios distintos: algunas obras
 *              sociales son más laxas (suspenden recién a los 90 días),
 *              otras más estrictas (60 días). El componente lee/escribe
 *              `organization.configuration.memberPaymentRules`.
 *
 *              Internamente el usuario edita SOLO 2 umbrales
 *              (`earlyEnd` y `lateEnd`); el componente arma el shape
 *              completo (con los rangos fromDays/toDays) que espera el API.
 *
 * @example <MemberPaymentRulesConfig />
 */
import organizationService from "@/services/organization";

const DEFAULTS = { earlyEnd: 30, lateEnd: 60, autoSuspend: true };

export default {
  name: "MemberPaymentRulesConfig",

  data() {
    return {
      isLoading: false,
      isSaving: false,
      /**
       * @type {number} día final del bloque "mora temprana" (inclusive).
       *                Ej. 30 → 1-30 = temprana.
       */
      earlyEnd: DEFAULTS.earlyEnd,
      /**
       * @type {number} día final del bloque "mora tardía" (inclusive).
       *                Ej. 60 → 31-60 = tardía, 61+ = suspendido.
       */
      lateEnd: DEFAULTS.lateEnd,
      /**
       * @type {boolean} si está ON, cuando paymentSituation pasa a
       *                 `suspended_unpaid`, status del afiliado pasa
       *                 automáticamente a `inactive` (no puede recibir
       *                 cobertura). Si está OFF, la situación se actualiza
       *                 pero el operador decide manualmente.
       */
      autoSuspend: DEFAULTS.autoSuspend,
    };
  },

  computed: {
    /**
     * @description Validación: los umbrales deben ser estrictamente
     *              crecientes y mayores a 0.
     * @returns {string|null} mensaje de error, o `null` si todo OK
     */
    validationError() {
      if (!Number.isInteger(this.earlyEnd) || this.earlyEnd < 1) {
        return "El fin de mora temprana debe ser un número entero ≥ 1.";
      }
      if (!Number.isInteger(this.lateEnd) || this.lateEnd <= this.earlyEnd) {
        return "El fin de mora tardía debe ser un entero mayor que el fin de mora temprana.";
      }
      return null;
    },
    canSave() {
      return !this.isSaving && !this.isLoading && !this.validationError;
    },
  },

  mounted() {
    this.loadRules();
  },

  methods: {
    /**
     * @description Lee la config actual del backend y la convierte al
     *              shape simplificado del form (earlyEnd / lateEnd).
     */
    async loadRules() {
      this.isLoading = true;
      try {
        const rules = await organizationService.getMemberPaymentRules();
        this.earlyEnd = rules.earlyDefault?.toDays ?? DEFAULTS.earlyEnd;
        this.lateEnd = rules.lateDefault?.toDays ?? DEFAULTS.lateEnd;
        this.autoSuspend =
          typeof rules.autoSuspend === "boolean"
            ? rules.autoSuspend
            : DEFAULTS.autoSuspend;
      } catch (err) {
        console.error("Error loading member payment rules:", err);
        this.$message({
          type: "error",
          message: "No se pudieron cargar las reglas de mora.",
        });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Guarda los rangos. Arma el shape completo
     *              earlyDefault/lateDefault/suspended con fromDays/toDays
     *              calculados a partir de earlyEnd y lateEnd.
     */
    async save() {
      if (!this.canSave) return;
      this.isSaving = true;
      const payload = {
        earlyDefault: { fromDays: 1, toDays: this.earlyEnd },
        lateDefault: {
          fromDays: this.earlyEnd + 1,
          toDays: this.lateEnd,
        },
        suspended: { fromDays: this.lateEnd + 1 },
        autoSuspend: this.autoSuspend,
      };
      try {
        await organizationService.updateMemberPaymentRules(payload);
        this.$message({
          type: "success",
          message: "Reglas de mora actualizadas correctamente.",
        });
      } catch (err) {
        const apiMessage = err?.response?.data?.error;
        console.error("Error saving member payment rules:", err);
        this.$message({
          type: "error",
          message: apiMessage || "No se pudieron guardar las reglas.",
        });
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * @description Vuelve los inputs a los defaults sugeridos (1-30 / 31-60 / 61+).
     */
    resetDefaults() {
      this.earlyEnd = DEFAULTS.earlyEnd;
      this.lateEnd = DEFAULTS.lateEnd;
      this.autoSuspend = DEFAULTS.autoSuspend;
    },
  },
};
</script>

<template lang="pug">
section.member-payment-rules
  .member-payment-rules__intro
    h3 Reglas de mora de afiliados
    p.member-payment-rules__desc
      | Configurá, en días desde el último pago registrado, en qué momento
      | un afiliado pasa a "mora temprana", "mora tardía" o "suspendido por
      | falta de pago". Los rangos son inclusivos y deben ser crecientes.
      | Si no se carga ninguna fecha de último pago, el afiliado queda al día.

  .member-payment-rules__skeleton(v-if="isLoading")
    el-skeleton(animated :rows="4")

  .member-payment-rules__form(v-else)
    .member-payment-rules__row
      .member-payment-rules__field
        label Fin de mora temprana (día)
        el-input-number(
          v-model="earlyEnd"
          :min="1"
          :max="999"
          :step="1"
          controls-position="right"
        )
        .member-payment-rules__hint
          | Días 1 a {{ earlyEnd }} → mora temprana.
      .member-payment-rules__field
        label Fin de mora tardía (día)
        el-input-number(
          v-model="lateEnd"
          :min="earlyEnd + 1"
          :max="999"
          :step="1"
          controls-position="right"
        )
        .member-payment-rules__hint
          | Días {{ earlyEnd + 1 }} a {{ lateEnd }} → mora tardía.
          | A partir del día {{ lateEnd + 1 }} → suspendido por falta de pago.

    .member-payment-rules__row
      .member-payment-rules__field
        label.member-payment-rules__switch-label
          el-switch(v-model="autoSuspend")
          span Auto-suspender afiliados al entrar en "suspendido por falta de pago"
        .member-payment-rules__hint
          | Si está activo, los afiliados que entran en "suspendido por falta
          | de pago" automáticamente pasan a estado <strong>Inactivo</strong>
          | y dejan de poder recibir cobertura. Si está apagado, la situación
          | de pago se actualiza pero el estado operativo lo cambia el operador.

    .member-payment-rules__preview
      .member-payment-rules__preview-title Vista previa
      .member-payment-rules__preview-grid
        .member-payment-rules__preview-cell.is-up Al día
          span.member-payment-rules__preview-range Sin atraso
        .member-payment-rules__preview-cell.is-early Mora temprana
          span.member-payment-rules__preview-range 1 – {{ earlyEnd }} días
        .member-payment-rules__preview-cell.is-late Mora tardía
          span.member-payment-rules__preview-range {{ earlyEnd + 1 }} – {{ lateEnd }} días
        .member-payment-rules__preview-cell.is-suspended Suspendido (falta de pago)
          span.member-payment-rules__preview-range {{ lateEnd + 1 }} días o más

    .member-payment-rules__error(v-if="validationError")
      micon(name="warning")
      span {{ validationError }}

    .member-payment-rules__actions
      el-button(
        type="text"
        @click="resetDefaults"
        :disabled="isSaving"
      ) Volver a defaults
      el-button(
        type="primary"
        @click="save"
        :loading="isSaving"
        :disabled="!canSave"
      ) Guardar cambios
</template>

<style lang="scss" scoped>
.member-payment-rules {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 8px 0;

  &__intro {
    h3 {
      margin: 0 0 8px;
      font-size: 18px;
      font-weight: 600;
      color: #142647;
    }
  }

  &__desc {
    margin: 0;
    color: #4a5b7a;
    font-size: 14px;
    line-height: 1.5;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 24px;
    background: #fff;
    border: 1px solid #e6eaf2;
    border-radius: 12px;
    padding: 24px;
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 240px;

    label {
      font-size: 13px;
      color: #142647;
      font-weight: 600;
    }
  }

  &__switch-label {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__hint {
    font-size: 12px;
    color: #6b7c99;
    line-height: 1.4;
  }

  &__preview {
    border-top: 1px dashed #e6eaf2;
    padding-top: 16px;

    &-title {
      font-size: 12px;
      font-weight: 600;
      color: #6b7c99;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      margin-bottom: 12px;
    }

    &-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 12px;
    }

    &-cell {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 12px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      color: #fff;

      &.is-up {
        background: #1e8e4f;
      }
      &.is-early {
        background: #e0a823;
      }
      &.is-late {
        background: #d36b1a;
      }
      &.is-suspended {
        background: #b03434;
      }
    }

    &-range {
      font-size: 12px;
      font-weight: 400;
      opacity: 0.85;
    }
  }

  &__error {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff4f4;
    color: #b03434;
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 13px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
}
</style>
